/**
 * FELVORA — Línea Inmobiliaria: inventario de inmuebles (F2 A1).
 * CRUD + galería + docs (recicla storage.py backend). Propietario referenciado por
 * cliente_id (búsqueda en db.clientes) — R2: cero recaptura.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Plus, Search, Trash2, Pencil, Upload, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useMiActividad } from "@/hooks/useMiActividad";
import { CampoBien } from "@/components/inmobiliaria/CampoBien";

const TIPOS = ["casa", "departamento", "local", "terreno", "oficina", "bodega"];
const OPERACIONES = ["venta", "renta", "ambas"];
const ESTATUS = ["disponible", "apartado", "vendido", "rentado", "retirado"];

const EMPTY = {
  direccion: "", tipo: "casa", operacion_destino: "venta",
  valor_venta: 0, valor_renta_mensual: 0, estatus: "disponible",
  m2_terreno: 0, m2_construccion: 0, recamaras: 0, banos: 0, estacionamientos: 0,
  notas: "", propietario_cliente_id: "", asesor_user_id: "",
  codigo_postal: "", colonia: "", municipio: "", estado_geo: "",
};

const mxn = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n || 0);

// ago-2026 · Formulario genérico — los valores iniciales salen de los campos que declara la
// actividad; los numéricos arrancan en 0 y el resto vacíos, igual que el formulario previo.
function vacioDeCampos(campos) {
  const base = { estatus: "disponible" };
  (campos || []).forEach((c) => {
    base[c.clave] = (c.tipo === "numero" || c.tipo === "moneda") ? 0
      : (c.tipo === "opcion" ? (c.requerido ? (c.opciones || [])[0] || "" : "") : "");
  });
  return base;
}

export default function Inmuebles() {
  const act = useMiActividad();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [estatusFiltro, setEstatusFiltro] = useState("");
  const [tipoFiltro, setTipoFiltro] = useState("");
  const [open, setOpen] = useState(false);
  const campos = act.config?.bien_campos || [];
  const vacio = vacioDeCampos(campos);
  const [form, setForm] = useState(EMPTY);
  const setCampo = (clave, valor) => setForm((f) => ({ ...f, [clave]: valor }));
  const [editing, setEditing] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [asesores, setAsesores] = useState([]);

  const load = async () => {
    const params = { ...(estatusFiltro && { estatus: estatusFiltro }), ...(tipoFiltro && { tipo: tipoFiltro }), ...(q && { q }) };
    const r = await api.get("/inmobiliaria/inmuebles", { params });
    setItems(r.data);
  };

  useEffect(() => {
    load();
    api.get("/clientes?limit=500").then((r) => setClientes(r.data || [])).catch(() => {});
    api.get("/inmobiliaria/asesores").then((r) => setAsesores(r.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estatusFiltro, tipoFiltro]);

  const submit = async () => {
    try {
      if (editing) {
        await api.put(`/inmobiliaria/inmuebles/${editing.id}`, form);
        toast.success(`${act.bien} actualizado`);
      } else {
        await api.post("/inmobiliaria/inmuebles", form);
        toast.success(`${act.bien} registrado`);
      }
      setOpen(false); setForm(vacio); setEditing(null); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    }
  };

  const eliminar = async (id) => {
    if (!window.confirm(`¿Eliminar este ${act.bien.toLowerCase()}? Solo procede si no tiene operaciones asociadas.`)) return;
    try {
      await api.delete(`/inmobiliaria/inmuebles/${id}`);
      toast.success(`${act.bien} eliminado`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al eliminar");
    }
  };

  const openEdit = (it) => {
    setEditing(it);
    setForm({ ...EMPTY, ...it });
    setOpen(true);
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="inmuebles-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">
            <Building2 className="mr-2 inline h-6 w-6 text-emerald-700" />
            Inventario de {act.bienes.toLowerCase()}
          </h1>
          <p className="text-sm text-slate-500">Fuente única del {act.bien.toLowerCase()}. Las operaciones lo referencian por id.</p>
        </div>
        <Button data-testid="btn-nuevo-inmueble" onClick={() => { setForm(vacio); setEditing(null); setOpen(true); }}>
          <Plus className="mr-2 h-4 w-4" /> Nuevo {act.bien.toLowerCase()}
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="input-search-inmuebles" placeholder={`Buscar ${(campos[0]?.label || act.bien).toLowerCase()} / propietario…`} value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()} className="w-72 pl-9" />
        </div>
        <Select value={estatusFiltro || "__all__"} onValueChange={(v) => setEstatusFiltro(v === "__all__" ? "" : v)}>
          <SelectTrigger data-testid="filter-estatus" className="w-44"><SelectValue placeholder="Estatus" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos los estatus</SelectItem>
            {ESTATUS.map((e) => <SelectItem key={e} value={e} className="capitalize">{e}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={tipoFiltro || "__all__"} onValueChange={(v) => setTipoFiltro(v === "__all__" ? "" : v)}>
          <SelectTrigger data-testid="filter-tipo" className="w-44"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos los tipos</SelectItem>
            {(campos.find((c) => c.clave === "tipo")?.opciones || TIPOS).map((t) => (
              <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">{items.length} resultado(s)</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {items.map((it) => (
              <Card key={it.id} data-testid={`inmueble-card-${it.id}`} className="border-slate-200">
                <CardContent className="space-y-2 p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-slate-900">
                        {it.direccion || it.descripcion_bien || "—"}
                      </p>
                      <p className="text-xs text-slate-500">
                        {it.direccion ? [it.colonia, it.municipio].filter(Boolean).join(" · ")
                                      : it.descripcion_bien}
                      </p>
                    </div>
                    {(it.tipo || it.tipo_bien) &&
                      <Badge variant="secondary" className="capitalize">{it.tipo || it.tipo_bien}</Badge>}
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <Badge className={`capitalize ${it.estatus === "disponible" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`}>{it.estatus}</Badge>
                    {it.operacion_destino &&
                      <Badge variant="outline" className="capitalize">{it.operacion_destino}</Badge>}
                  </div>
                  <div className="text-sm text-slate-700">
                    {(it.operacion_destino === "venta" || it.operacion_destino === "ambas") &&
                      <div>Venta: <span className="font-semibold">{mxn(it.valor_venta)}</span></div>}
                    {(it.operacion_destino === "renta" || it.operacion_destino === "ambas") &&
                      <div>Renta/mes: <span className="font-semibold">{mxn(it.valor_renta_mensual)}</span></div>}
                  </div>
                  <p className="text-xs text-slate-500">Propietario: <span className="font-medium text-slate-700">{it.propietario_nombre_snap || "—"}</span></p>
                  <div className="flex justify-end gap-1 pt-2">
                    <Button data-testid={`btn-editar-${it.id}`} size="icon" variant="ghost" onClick={() => openEdit(it)}><Pencil className="h-4 w-4" /></Button>
                    <Button data-testid={`btn-eliminar-${it.id}`} size="icon" variant="ghost" onClick={() => eliminar(it.id)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {items.length === 0 && <p className="col-span-full text-center text-sm text-slate-500 py-6">No hay {act.bienes.toLowerCase()} aún.</p>}
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{editing ? `Editar ${act.bien.toLowerCase()}` : `Nuevo ${act.bien.toLowerCase()}`}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="bien-form-campos">
            {campos.map((c) => (
              <CampoBien key={c.clave} campo={c} valor={form[c.clave]} onChange={setCampo}
                         clientes={clientes} asesores={asesores} />
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button data-testid="btn-guardar-inmueble" onClick={submit}>{editing ? "Guardar cambios" : `Registrar ${act.bien.toLowerCase()}`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
